import Image from 'next/image';

import {
  CartLine,
  removeFromCart,
  updateCartLines,
} from '@/clients/storefront/storefrontClient';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { IconName } from '@/components/Icon/Icon';
import QuantityPicker from '@/components/QuantityPicker/QuantityPicker';
import { getFormattedPrice } from '@/components/SunrunShop/utils/price';
import { useSunrunShopActions } from '@/data-store/hooks/useSunrunShopStore';
import { CartActions } from '@/data-store/slices/sunrunShopSlice';
import useGlobalStyles from '@/styles/global.styles';
import shopifyImageLoader from '@/utils/imageLoaders/shopify';

import useStyles from './CartItem.style';

export default function CartItem({
  cartId,
  item,
}: {
  item: CartLine;
  cartId: string;
}) {
  const { setCart, setCartAction } = useSunrunShopActions();
  const {
    merchandise: { image, price, product, title },
    quantity,
    id: lineId,
  } = item;
  const { classes, cx } = useStyles();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });

  const formattedPrice = getFormattedPrice(price, quantity);

  const removeItem = async () => {
    try {
      const updatedCart = await removeFromCart(cartId, [item.id]);
      setCart(updatedCart);
      setCartAction(CartActions.REMOVE_ITEM);
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleQtyChange = async (updatedQty: number) => {
    try {
      const updatedCart = await updateCartLines(cartId, [
        { id: lineId, quantity: updatedQty },
      ]);
      setCart(updatedCart);
      setCartAction(CartActions.UPDATE_QTY);
    } catch (err) {
      console.error('Error updating qty for item:', err);
    }
  };

  return (
    <div className={classes.container}>
      <Image
        src={image?.url || ''}
        alt={image?.altText || 'Missing image'}
        width={100}
        height={100}
        loader={shopifyImageLoader}
        className={classes.thumbnail}
      />

      <div className={classes.midSection}>
        <div className={classes.nameAndDescription}>
          <p className={globalClasses.body}>{product?.title || title}</p>
          <span className={cx(classes.description, globalClasses.body)}>
            {title}
          </span>
        </div>

        <div className={classes.qtyAndRemove}>
          <QuantityPicker
            key={quantity}
            initialValue={quantity}
            onChange={handleQtyChange}
          />
          <Button
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.XS}
            onClick={removeItem}
            iconName={IconName.TRASH_CAN}
            className={classes.removeBtn}
            aria-label='Remove from cart'
          />
        </div>
      </div>

      <div className={cx(classes.price, globalClasses.body)}>
        {formattedPrice}
      </div>
    </div>
  );
}
